import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';

import { HStack } from './HStack';
import { NewButton } from './NewButton';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Input } from './input';
import { Textarea } from './textarea';

export interface InlineEditProps {
  value: string;
  onSave: (newValue: string) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  children: React.ReactNode;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  isLoading = false,
  isReadOnly = false,
  multiline = false,
  placeholder,
  className,
  maxLength,
  children,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    const InputComponent = multiline ? Textarea : Input;

    return (
      <div className={className} onClick={(e) => e.stopPropagation()}>
        <InputComponent
          value={editValue}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setEditValue(e.target.value)
          }
          placeholder={placeholder}
          className="mb-2"
          rows={multiline ? 3 : undefined}
          maxLength={maxLength}
          onKeyDown={(
            e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            if (e.key === 'Enter' && !multiline) {
              handleSave();
            }
            if (e.key === 'Escape') {
              handleCancel();
            }
          }}
        />
        <HStack>
          <NewButton
            size="s"
            type="primary"
            text="Save"
            onClick={handleSave}
            disabled={isLoading}
            icon={{ name: 'Check' }}
          />
          <NewButton
            size="s"
            type="outline"
            text="Cancel"
            onClick={handleCancel}
            disabled={isLoading}
            icon={{ name: 'X' }}
          />
        </HStack>
      </div>
    );
  }

  return (
    <HStack className={className}>
      {children}
      {!isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <NewButton
              size="s"
              type="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              icon={{ name: 'Pencil' }}
            />
          </TooltipTrigger>
          <TooltipContent>{`Edit ${placeholder?.toLowerCase()}`}</TooltipContent>
        </Tooltip>
      )}
    </HStack>
  );
};
